'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CelebrationModal } from './celebration-modal';
import { celebrationPriority, describeCelebration, type CelebrationEvent } from './celebration-events';

interface CelebrationContextValue {
  enqueue: (events: CelebrationEvent[]) => void;
  trigger: (event: CelebrationEvent) => void;
}

const CelebrationContext = createContext<CelebrationContextValue | null>(null);

const MAX_MODALS_PER_SESSION = 3;

interface CelebrationProviderProps {
  employeeId: string;
  children: React.ReactNode;
}

export function CelebrationProvider({ employeeId, children }: CelebrationProviderProps) {
  const [queue, setQueue] = useState<CelebrationEvent[]>([]);
  const [current, setCurrent] = useState<CelebrationEvent | null>(null);
  const [open, setOpen] = useState(false);
  const shownCountRef = useRef(0);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const seenKey = `portal:celebrations:seen:${employeeId}`;
  const lastToneRef = useRef<'happy' | 'info' | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(seenKey);
      if (raw) seenIdsRef.current = new Set(JSON.parse(raw));
    } catch {}
  }, [seenKey]);

  const markSeen = useCallback((id: string) => {
    seenIdsRef.current.add(id);
    try {
      localStorage.setItem(seenKey, JSON.stringify([...seenIdsRef.current]));
    } catch {}
  }, [seenKey]);

  const forcedIdsRef = useRef<Set<string>>(new Set());

  const enqueue = useCallback((events: CelebrationEvent[]) => {
    const fresh = events.filter((e) => !seenIdsRef.current.has(e.id));
    if (!fresh.length) return;
    fresh.forEach((e) => markSeen(e.id));
    setQueue((q) => [...q, ...fresh].sort((a, b) => celebrationPriority[b.kind] - celebrationPriority[a.kind]));
  }, [markSeen]);

  const trigger = useCallback((event: CelebrationEvent) => {
    forcedIdsRef.current.add(event.id);
    setQueue((q) => [...q, event]);
  }, []);

  const advance = useCallback(() => {
    setQueue((q) => {
      if (!q.length) {
        setCurrent(null);
        setOpen(false);
        return q;
      }

      const [next, ...rest] = q;
      const forced = forcedIdsRef.current.has(next.id);
      const tone = describeCelebration(next).tone;

      if (!forced && shownCountRef.current >= MAX_MODALS_PER_SESSION) {
        q.forEach((e) => {
          if (forcedIdsRef.current.has(e.id)) return;
          const copy = describeCelebration(e);
          if (copy.tone === 'happy') toast.success(copy.headline, { description: copy.body.replace(/\*\*/g, '') });
          else toast(copy.headline, { description: copy.body.replace(/\*\*/g, '') });
        });
        const remaining = q.filter((e) => forcedIdsRef.current.has(e.id));
        if (remaining.length) {
          const [forcedNext, ...forcedRest] = remaining;
          shownCountRef.current += 1;
          setCurrent(forcedNext);
          setOpen(true);
          lastToneRef.current = describeCelebration(forcedNext).tone;
          return forcedRest;
        }
        setCurrent(null);
        setOpen(false);
        return [];
      }

      if (!forced && lastToneRef.current === 'info' && tone === 'info') {
        const copy = describeCelebration(next);
        toast(copy.headline, { description: copy.body.replace(/\*\*/g, '') });
        if (rest.length) {
          const [afterNext, ...afterRest] = rest;
          shownCountRef.current += 1;
          setCurrent(afterNext);
          setOpen(true);
          lastToneRef.current = describeCelebration(afterNext).tone;
          return afterRest;
        }
        setCurrent(null);
        setOpen(false);
        return [];
      }

      shownCountRef.current += 1;
      setCurrent(next);
      setOpen(true);
      lastToneRef.current = tone;
      return rest;
    });
  }, []);

  useEffect(() => {
    if (!open && queue.length && !current) {
      advance();
    }
  }, [queue, open, current, advance]);

  useEffect(() => {
    if (!current && queue.length) advance();
  }, [current, queue.length, advance]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setCurrent(null);
      advance();
    }, 180);
  };

  const handleCta = (event: CelebrationEvent) => {
    const copy = describeCelebration(event);
    if (copy.ctaHref?.startsWith('#')) {
      window.dispatchEvent(new CustomEvent('portal:navigate', { detail: { target: copy.ctaHref.slice(1) } }));
    }
    handleClose();
  };

  const value = useMemo(() => ({ enqueue, trigger }), [enqueue, trigger]);

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      <CelebrationModal event={current} open={open} onClose={handleClose} onCta={handleCta} />
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  const ctx = useContext(CelebrationContext);
  if (!ctx) throw new Error('useCelebration must be used inside CelebrationProvider');
  return ctx;
}

